/**
 * supers.ts — per-hero super-move definitions.
 *
 * Consumed by Player (combat) and StageScene (visual effects).
 * No Phaser import — plain typed data.
 */
import type { HitSpec } from '@/systems/Combat';

export interface SuperDef {
  /** Flat damage to every enemy inside the blast radius. */
  damage: number;
  /** Pixel radius from attacker centre (screen-clear zone). */
  radius: number;
  /** Hitstun applied on hit (frames). */
  hitstun: number;
  /** Knockback magnitude (px), directed away from attacker. */
  knockback: number;
  /** Animation key token — wired to sprite playback when art lands (MY-1441+). */
  animKey: string;
}

/** Screen-freeze duration (frames) matching the issue spec. */
export const SUPER_FREEZE_FRAMES = 8;

export function superToHitSpec(s: SuperDef): HitSpec {
  return { damage: s.damage, knockback: s.knockback, hitstun: s.hitstun, isSuper: true };
}

const HERO_SUPERS: Record<string, SuperDef> = {
  ray: {
    damage: 40,
    radius: 220,
    hitstun: 36,
    knockback: 60,
    animKey: 'super_ray',
  },
  errol: {
    damage: 35,
    radius: 240,
    hitstun: 32,
    knockback: 50,
    animKey: 'super_errol',
  },
  kris: {
    damage: 38,
    radius: 200,
    hitstun: 34,
    knockback: 55,
    animKey: 'super_kris',
  },
  priya: {
    damage: 32,
    radius: 260,
    hitstun: 30,
    knockback: 45,
    animKey: 'super_priya',
  },
  ghost: {
    damage: 45,
    radius: 180,
    hitstun: 40,
    knockback: 70,
    animKey: 'super_ghost',
  },
  praetor: {
    damage: 48,
    radius: 210,
    hitstun: 42,
    knockback: 72,
    animKey: 'super_praetor',
  },
  // "Call Security" — a Praetorian appears and immediately walks off screen (gag).
  // Near-zero stats: Halloway's super is a joke, not a power move (MY-2002).
  halloway: {
    damage: 0,
    radius: 0,
    hitstun: 0,
    knockback: 0,
    animKey: 'super_halloway',
  },
};

const DEFAULT_SUPER: SuperDef = {
  damage: 35,
  radius: 220,
  hitstun: 32,
  knockback: 50,
  animKey: 'super_default',
};

export function getHeroSuper(heroId: string): SuperDef {
  return HERO_SUPERS[heroId] ?? DEFAULT_SUPER;
}
